/**
 * The wheel's geometry — the part that has to be right for the pointer to land on the prize it names.
 *
 * The server draws the prize; the browser only animates towards a segment it has already been told.
 * Getting this arithmetic wrong means the wheel visibly stops on one slice and the screen announces a
 * different one, which reads as the game being rigged.
 *
 * Legacy's convention, kept: segment `i` is drawn starting at `rotation + i × arc`, and the pointer sits
 * at the top — at `-π/2`. So aligning a segment's **centre** under the pointer needs
 *
 * ```
 * rotation + i × arc + arc / 2 = -π / 2
 * ```
 *
 * which is the identity `targetRotation` implements. The extra whole turns are cosmetic and are added
 * afterwards, so they cannot change where it stops.
 */

export const SPIN_DURATION_MS = 4000
/** Whole turns before settling, so it looks like a wheel rather than a needle. */
export const SPIN_TURNS = 5

export interface WheelSegment {
  label: string
  color: string
}

/** The angle one segment occupies, in radians. */
export function segmentArc(count: number): number {
  return count > 0 ? (Math.PI * 2) / count : 0
}

/**
 * Where the wheel must end up for `index` to sit under the pointer.
 *
 * Answers `null` for an index the wheel does not have — better a wheel that does not spin than one that
 * stops on a slice chosen by an out-of-range calculation.
 */
export function targetRotation(index: number, count: number): number | null {
  if (!Number.isInteger(index) || index < 0 || index >= count || count <= 0) return null
  const arc = segmentArc(count)
  return -index * arc - arc / 2 - Math.PI / 2
}

/** Whole extra turns added to the target, so the wheel spins before it settles. */
export function spinDestination(from: number, index: number, count: number): number | null {
  const target = targetRotation(index, count)
  if (target === null) return null
  // Always forward from where it is now, so a second spin never runs backwards.
  const turns = Math.ceil((from - target) / (Math.PI * 2)) + SPIN_TURNS
  return target + turns * Math.PI * 2
}

/** Ease-out cubic — fast away, slow into the prize, which is what makes the stop legible. */
export function easeOut(progress: number): number {
  const clamped = Math.min(1, Math.max(0, progress))
  return 1 - Math.pow(1 - clamped, 3)
}

/** The rotation at a moment in the spin. */
export function rotationAt(from: number, to: number, elapsedMs: number): number {
  return from + (to - from) * easeOut(elapsedMs / SPIN_DURATION_MS)
}

/**
 * Which segment is currently under the pointer.
 *
 * Only used to check the animation agreed with the answer — if this does not return the index the
 * server sent, the wheel is lying.
 */
export function segmentUnderPointer(rotation: number, count: number): number {
  if (count <= 0) return -1
  const arc = segmentArc(count)
  // Undo the identity in `targetRotation`, then bring it back into range.
  const raw = (-rotation - Math.PI / 2 - arc / 2) / arc
  // `+ count` before the modulo rather than a sign test afterwards: `Math.round` answers `-0` for a
  // small negative, and `-0 < 0` is false, so the sign test would let `-0` through.
  return ((Math.round(raw) % count) + count) % count
}
